import styled from 'styled-components';

import { Bucket as BucketRaw } from '@src/components/Bucket';
import { setAlpha, SideEnum, Text } from '@talos/kyoko';

export const TOB_PRICE_HEIGHT = 60;

export const PriceWrapper = styled.button.attrs(() => ({ type: 'button' }))<{
  active: boolean;
  side: string;
  isReadOnly: boolean;
}>`
  padding: 0;
  height: auto;
  text-align: center;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ active, side, isReadOnly, theme }) => `
    background: ${active ? (side === SideEnum.Sell ? theme.colors.red.dim : theme.colors.green.dim) : ''};
    cursor: ${isReadOnly ? 'initial' : 'pointer'};

    :hover {
      background: ${isReadOnly || active ? '' : setAlpha(0.05, theme.colors.gray['100'])};

      ${Label} {
        opacity: 1;
      }
    }
  `}
  :focus > * {
    text-shadow: ${({ isReadOnly }) => !isReadOnly && '0 0 1px currentColor'};
  }
`;

export const Price = styled.span`
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing2}px;
  color: ${({ theme }) => theme.colorTextImportant};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const AdditionalText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray['090']};
  background-color: ${({ theme }) => setAlpha(0.08, theme.colors.gray['100'])};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  padding: ${({ theme }) => theme.spacing2}px ${({ theme }) => theme.spacing4}px;
  line-height: ${({ theme }) => theme.fontSizeSmall}rem;
  transition: font-size 200ms ease;
`;
AdditionalText.defaultProps = {
  position: 'absolute',
  top: 42,
};

export const Wrapper = styled.div<{ highlightBBO?: boolean }>`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing4}px;

  @keyframes pulseBackground {
    0%,
    50%,
    100% {
    }
    25%,
    75% {
      background-color: ${({ theme }) => theme.colors.yellow.dim};
    }
  }

  @keyframes pulseColor {
    0%,
    50%,
    100% {
    }
    25%,
    75% {
      color: ${({ theme }) => theme.colors.yellow.lighten};
    }
  }

  @keyframes notice {
    0% {
      background-color: inherit;
      color: ${({ theme }) => theme.colorTextImportant};
    }
    25%,
    100% {
      color: ${({ theme }) => theme.colors.yellow.lighten};
      background-color: ${({ theme }) => theme.colors.yellow.dim};
    }
  }

  ${({ highlightBBO: useBBO }) =>
    useBBO &&
    `${PriceWrapper} {
      animation: pulseBackground 1500ms ease-in forwards;

      ${Price} * {
        animation: pulseColor 1500ms ease-in forwards;
      }
    }

    ${AdditionalText} {
      animation: notice 1500ms ease-in forwards;
    }
  `}
`;

export const Currencies = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1 0 auto;
  line-height: 0;
`;

export const Currency = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  margin: 0 3px;
  & > svg {
    margin-right: 3px;
  }
`;

export const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.gray['080']};
  text-transform: uppercase;
  padding-top: ${({ theme }) => theme.spacing4}px;
`;

export const Label = styled.div<{ showLabel: string }>`
  text-transform: uppercase;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray['090']};
  padding: ${({ theme }) => theme.spacing4}px 0;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  align-items: center;
  transition: opacity 200ms ease;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;

  ${({ showLabel }) =>
    `
      opacity: ${showLabel === 'always' ? 1 : 0};
      display: ${showLabel === 'never' ? 'none' : 'flex'};
      visibility: ${showLabel === 'hidden' ? 'hidden' : 'visible'};
    `}
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: ${({ theme }) => theme.spacing4}px;
  color: ${({ theme }) => theme.colors.gray['090']};
`;

export const Indicator = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;
  align-items: center;
`;

export const Side = styled.div`
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.green.lighten};
`;

export const Bucket = styled(BucketRaw)<{ isReadOnly?: boolean }>`
  ${({ isReadOnly, theme }) =>
    isReadOnly &&
    `
    &, &:hover {
      border-color: 1px solid ${theme.colors.gray['070']};
      opacity: .5;
    }
  `}
`;
